import * as React from "react";

import { faCalendarDays, faFilter, faFilterSlash } from "@fortawesome/sharp-regular-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  Alert,
  BackendFailureAlert,
  Box,
  Button,
  ControlBar,
  DataGrid7 as DataGrid,
  DateRangePicker,
  Divider,
  focusRing,
  Grid2 as Grid,
  GridColDef,
  GridOverlay,
  GridRenderCellParams,
  IconButton,
  LinearProgress,
  Paper,
  Popover,
  pxToRem,
  Snackbar,
  TextField,
  Tooltip,
  useTheme,
} from "@platform/style";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { format, parseISO } from "date-fns";
import { WorkGroup } from "../../models/WorkGroup";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { WorkGroupRowMenu } from "./WorkGroupRowMenu";
import { CreateWorkGroup } from "./CreateWorkGroup";
import { hasPermission, useGetWithToken } from "@platform/core";
import { WORK_MANAGEMENT_ADMIN_BFF_API_BASE } from "../../platform-workitems";

library.add(faFilter, faCalendarDays, faFilterSlash);

function CustomNoRowsOverlay() {
  return (
    <GridOverlay>
      <div>No Work Groups to Display.</div>
    </GridOverlay>
  );
}

export default function WorkGroupsGrid() {
  const [workGroupResponse, setWorkGroupResponse] = React.useState<WorkGroup[]>([]);
  const [nameDescription, setNameDescription] = React.useState<string>("");
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [filterOption, setFilterOption] = React.useState<string>("");
  const [selectDataGridView, setSelectDataGridView] = React.useState<boolean>(true);
  const [currentWorkGroup, setCurrentWorkGroup] = React.useState<WorkGroup | undefined>(undefined);
  const [showMessage, setShowMessage] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [workGroupsActionError, setWorkGroupsActionError] = React.useState<string | null>(null);
  const [isSearchFilterOn, setIsSearchFilterOn] = React.useState<boolean>(false);
  const [isDateFilterOn, setIsDateFilterOn] = React.useState<boolean>(false);
  const [allowAssignPeopleGroup, setAllowAssignPeopleGroup] = React.useState<boolean>(false);
  const [allowAssignTemplatesOrGroup, setAllowAssignTemplatesOrGroup] =
    React.useState<boolean>(false);
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  React.useEffect(() => {
    (async () => {
      const assignPeopleGroupPermission = await hasPermission(
        "WorkManagementAdmin.assignPeopleGroup"
      );
      const assignTemplatesOrGroupPermission = await hasPermission(
        "WorkManagementAdmin.assignTemplatesOrGroup"
      );
      setAllowAssignPeopleGroup(assignPeopleGroupPermission);
      setAllowAssignTemplatesOrGroup(assignTemplatesOrGroupPermission);
    })();
  }, []);
  function handleViewChange(grp: WorkGroup) {
    setCurrentWorkGroup(grp);
    setWorkGroupsActionError(null);
    setSelectDataGridView(false);
    setIsSearchFilterOn(false);
    setIsDateFilterOn(false);
    refetch();
  }

  function openCreateWorkGroup() {
    setCurrentWorkGroup(undefined);
    setSelectDataGridView(false);
    setIsSearchFilterOn(false);
    setIsDateFilterOn(false);
    refetch();
  }

  function handleCancel() {
    setWorkGroupsActionError(null);
    setCurrentWorkGroup(undefined);
    setSelectDataGridView(true);
  }

  function handleSnackBar(message: string, open: boolean) {
    setMessage(message);
    setShowMessage(open);
  }

  function handleAlert(errorMessage: string) {
    setWorkGroupsActionError(errorMessage);
  }

  function handleDateChange(startDate: Date, endDate: Date) {
    const dateFilteredGroups = workGroupResponse.filter((group) => {
      return (
        !!group.Audit.LastUpdatedDatetime &&
        parseISO(group.Audit.LastUpdatedDatetime.toString()) >= startDate &&
        parseISO(group.Audit.LastUpdatedDatetime.toString()) <= endDate
      );
    });

    if (dateFilteredGroups.length > 0) {
      setWorkGroupResponse(dateFilteredGroups);
      setIsDateFilterOn(true);
    }
  }

  const closeWorkGroupsActionError = () => {
    setWorkGroupsActionError(null);
  };

  const getGroup = (id: string) => {
    return workGroupResponse.find((group) => {
      return !!id && group.WorkGroupIdentifier == id;
    });
  };

  const columns: GridColDef[] = [
    {
      field: "Name",
      headerName: "Name",
      flex: 0.7,

      renderCell: (params: GridRenderCellParams) => (params.value ? params.value : "--"),
    },
    {
      field: "Resources",
      headerName: "People in group",
      flex: 0.7,
      renderCell: (params: GridRenderCellParams) => (params.value as string)?.length,
      sortComparator: (v1, v2) => v1.length - v2.length,
    },
    {
      field: "Description",
      headerName: "Description",
      flex: 0.9,
      renderCell: (params: GridRenderCellParams) => (params.value ? params.value : "--"),
    },
    {
      field: "Templates",
      headerName: "Assoc. Templates",

      flex: 0.9,
      renderCell: (params: GridRenderCellParams) => (params.value as string)?.length,
      sortComparator: (v1, v2) => v1.length - v2.length,
    },
    {
      field: "LastUpdatedBy",
      headerName: "Last updated by",
      flex: 0.9,
      renderCell: (params: GridRenderCellParams) =>
        params.row.Audit?.LastUpdatedBy ? params.row.Audit.LastUpdatedBy : "--",
      sortComparator: (v1, v2, cellParams1, cellParams2) => {
        const name1 =
          cellParams1.api.getRow(cellParams1.id)?.Audit?.LastUpdatedBy?.toLowerCase() || "";
        const name2 =
          cellParams2.api.getRow(cellParams2.id)?.Audit?.LastUpdatedBy?.toLowerCase() || "";
        return name1.localeCompare(name2);
      },
    },
    {
      field: "LastUpdatedDateTime",
      headerName: "Last updated date",
      flex: 0.7,
      renderCell: (params: GridRenderCellParams) =>
        params.row.Audit.LastUpdatedDatetime
          ? format(parseISO(params.row.Audit.LastUpdatedDatetime), "M/d/yyyy")
          : "--",
      sortComparator: (v1, v2, cellParams1, cellParams2) => {
        const date1 = cellParams1.api.getRow(cellParams1.id)?.Audit?.LastUpdatedDatetime
          ? new Date(cellParams1.api.getRow(cellParams1.id).Audit.LastUpdatedDatetime).getTime()
          : 0;
        const date2 = cellParams2.api.getRow(cellParams2.id)?.Audit?.LastUpdatedDatetime
          ? new Date(cellParams2.api.getRow(cellParams2.id).Audit.LastUpdatedDatetime).getTime()
          : 0;
        return date1 - date2;
      },
    },

    {
      field: "WorkGroupIdentifier",
      headerName: "",
      sortable: false,
      flex: 0.2,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <WorkGroupRowMenu
            selectedGroup={getGroup(params.id.toString())!}
            viewChange={handleViewChange}
            snackBarDetails={handleSnackBar}
            onError={handleAlert}
            refetch={refetch}
          />
        );
      },
    },
  ];

  const handleAnchorEl = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSearchClick = () => {
    const filteredGroups = workGroupResponse.filter((group) => {
      return (
        !!group.Name &&
        !!group.Description &&
        nameDescription != "" &&
        (group.Name.toLowerCase().includes(nameDescription.toLowerCase()) ||
          group.Description.toLowerCase().includes(nameDescription.toLowerCase()))
      );
    });

    if (filteredGroups.length > 0) {
      setWorkGroupResponse(filteredGroups);
      setIsSearchFilterOn(true);
    }
  };

  const [data, isLoading, isErrored, refetch] = useGetWithToken<WorkGroup[] | null>(
    `${WORK_MANAGEMENT_ADMIN_BFF_API_BASE}/work-groups`,
    []
  );

  React.useEffect(() => {
    if (data) {
      setWorkGroupResponse(data);
    }
  }, [data]);

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          backgroundColor: "inherit",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ControlBar
          leftSlotComponents={
            <>
              <Tooltip title="Date Range">
                <IconButton
                  disabled={!selectDataGridView}
                  size={"small"}
                  aria-label={"Date Range"}
                  sx={{ padding: 1 }}
                  onClick={(e) => {
                    setFilterOption("DateRange");
                    handleAnchorEl(e);
                  }}
                >
                  <span className="fa-layers fa-fw">
                    <FontAwesomeIcon icon={faCalendarDays} data-testid={"CloseCalendarIcon"} />
                    {isDateFilterOn && (
                      <Box sx={[{ color: "primary.main" }]}>
                        <FontAwesomeIcon
                          icon={faCircle}
                          transform="shrink-10 right-8 up-8"
                          data-testid="filterWarningIcon"
                        />
                      </Box>
                    )}
                  </span>
                </IconButton>
              </Tooltip>
              <Tooltip title="Criteria">
                <IconButton
                  disabled={!selectDataGridView}
                  size={"small"}
                  aria-label={"Criteria"}
                  sx={{ padding: 1 }}
                  onClick={(e) => {
                    setFilterOption("Criteria");
                    handleAnchorEl(e);
                  }}
                >
                  <span className="fa-layers fa-fw">
                    <FontAwesomeIcon icon={faFilter} data-testid={"CloseFilterIcon"} />
                    {isSearchFilterOn && (
                      <Box sx={[{ color: "primary.main" }]}>
                        <FontAwesomeIcon
                          icon={faCircle}
                          transform="shrink-10 right-8 up-8"
                          data-testid="filterWarningIcon"
                        />
                      </Box>
                    )}
                  </span>
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear Filter">
                <IconButton
                  disabled={!selectDataGridView}
                  size={"small"}
                  aria-label={"Clear Filter"}
                  sx={{ padding: 1 }}
                  onClick={(_e) => {
                    refetch();
                    setIsSearchFilterOn(false);
                    setIsDateFilterOn(false);
                  }}
                >
                  <FontAwesomeIcon icon={faFilterSlash} data-testid={"CloseSlashFilterIcon"} />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" sx={{ paddingRight: 2, marginRight: 1 }} flexItem />
              {allowAssignPeopleGroup && allowAssignTemplatesOrGroup && (
                <Tooltip title="Add work group">
                  <IconButton
                    disabled={false}
                    size={"small"}
                    aria-label={"Add work group"}
                    sx={{ padding: 1 }}
                    onClick={openCreateWorkGroup}
                  >
                    <PersonAddAltIcon></PersonAddAltIcon>
                  </IconButton>
                </Tooltip>
              )}
            </>
          }
        ></ControlBar>
        {isLoading ? <LinearProgress sx={{ mt: 1 }} /> : <Box sx={{ mt: 1, height: "4px" }}></Box>}
        {workGroupsActionError && (
          <Alert
            onClose={closeWorkGroupsActionError}
            severity="error"
            sx={{
              width: "calc(100% - 34px)",
              borderColor: "error.main",
              borderWidth: "1px",
              borderStyle: "solid",
            }}
          >
            {workGroupsActionError}
          </Alert>
        )}
        {isErrored ? (
          <Box sx={{ mt: 1 }}>
            <BackendFailureAlert retryFunction={refetch} />
          </Box>
        ) : selectDataGridView ? (
          <DataGrid
            sx={{ maxHeight: 500 }}
            disableColumnMenu
            columnBufferPx={7}
            rows={workGroupResponse}
            rowCount={workGroupResponse.length}
            getRowId={(row) => row.WorkGroupIdentifier}
            rowHeight={60}
            columns={columns}
            data-testid={"DataGrid"}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
          />
        ) : (
          <CreateWorkGroup
            workGrp={currentWorkGroup}
            handleCancelClick={handleCancel}
            snackBarDetails={handleSnackBar}
            onError={handleAlert}
            refetch={refetch}
          ></CreateWorkGroup>
        )}
        {
          <Snackbar
            message={message}
            open={showMessage}
            onClose={() => {
              setShowMessage(false);
            }}
          />
        }

        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          sx={{ width: "310px" }}
        >
          <Paper
            sx={{
              p: 2,
              borderRadius: pxToRem(4),
              overflowY: "auto",
              maxHeight: "calc(100vh - 540px)",
              backgroundColor: useTheme().palette.background.default,
              width: "280px",
            }}
          >
            {(() => {
              if (filterOption == "DateRange") {
                return (
                  <DateRangePicker
                    key={new Date().getDate()}
                    disableFuture={false}
                    onSubmit={handleDateChange}
                  />
                );
              }
              if (filterOption == "Criteria") {
                return (
                  <Grid>
                    <Box sx={{ paddingBottom: "20px", paddingTop: "15px", paddingLeft: "10px" }}>
                      <TextField
                        id="NameDescription"
                        name={"NameDescription"}
                        label={"Name or Description"}
                        size={"small"}
                        slotProps={{ inputLabel: { shrink: true } }}
                        value={nameDescription}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          setNameDescription(event.target.value);
                        }}
                      />
                    </Box>
                    <Box sx={{ paddingBottom: "10px", paddingLeft: "10px" }}>
                      <Button
                        variant={"outlined"}
                        color="primary"
                        onClick={handleSearchClick}
                        style={{
                          borderRadius: "4px",
                          ...focusRing,
                          height: 30,
                          width: 100,
                        }}
                      >
                        Search
                      </Button>
                    </Box>
                  </Grid>
                );
              }
            })()}
          </Paper>
        </Popover>
      </Paper>
    </>
  );
}
